import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Role } from '../../../enum/role.enum';
import { StorageService } from '../../storage.service';
import { WalletTransactionFilter } from '../../../../pages/mysec/wallet/wallet-transaction-filter';
import { PagedResult } from '../../../../shared/models/paged-result/paged-result';
import { TransactionList } from '../../../models/mysec/transaction-list';
import { Wallet } from '../../../models/mysec/wallet';
import { HeldOpenOrders } from '../../../models/mysec/held-open-order';
import { HeldEcfInvestments } from '../../../models/mysec/held-ecf-investment';
import { Payment, WalletPayment } from '../../../models/payment/payment';
import { PaymentMethod } from '../../../models/payment/payment-method';
import { PaymentMethodModel } from '../../../models/payment/payment-method.model';
import { PaymentModel } from '../../../models/payment/payment.model';

@Injectable()
export class WalletService {
  private route = environment.apiServer + 'Api/v1/MySec/{role}/wallets';

  constructor(private http: HttpClient, private storageService: StorageService) {}

  activateWallet(): Observable<any> {
    return this.http.post(this.route + '/activate', null);
  }

  getWallet(): Observable<Wallet> {
    if (this.storageService.role === Role.IssuerRep) {
      return EMPTY;
    }

    return this.http.get(this.route).pipe(
      map((data: any) => {
        return new Wallet(data.result);
      }),
    );
  }

  depositWallet(amount: number, paymentModel: WalletPayment & Partial<Payment>): Observable<any> {
    //let body = new PaymentModel(paymentModel);
    //body.amount = +amount;
    let body = { ...paymentModel, amount: +amount };

    return this.http.post(this.route + '/deposit', body).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  withdrawWallet(model: PaymentMethod): Observable<any> {
    const body = new PaymentMethodModel(model);

    return this.http.post(this.route + '/withdraw', body).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getWalletTransaction(filter: WalletTransactionFilter): Observable<PagedResult<TransactionList>> {
    const params: any = Object.assign({}, filter);

    return this.http.get(this.route + '/history', { params: params }).pipe(
      map((data: any) => {
        return {
          data:
            data.result && data.result.data
              ? data.result.data.map((item) => new TransactionList(item))
              : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  cancelTransaction(walletTransactionHistoryId: number): Observable<any> {
    return this.http.put(this.route + '/' + walletTransactionHistoryId + '/cancel', null);
  }

  /**
   * #122 / #124: the cash-locking open orders behind the "PSTX Open Orders" hold
   * bucket. Sibling of getWallet() — same {role} base path (resolves to
   * individualInvestor / companyRep via the HTTP interceptor) + '/heldOpenOrders'.
   * The list reconciles to `openOrdersHoldAmount` server-side; render it, don't re-sum.
   */
  getHeldOpenOrders(): Observable<HeldOpenOrders> {
    if (this.storageService.role === Role.IssuerRep) {
      return EMPTY;    //of(new HeldOpenOrders(null));
    }

    return this.http.get(this.route + '/heldOpenOrders').pipe(
      map((data: any) => {
        return new HeldOpenOrders(data.result);
      }),
    );
  }

  /**
   * The wallet-held Paid ECF pledges behind the "ECF investment" hold bucket.
   * Sibling of getHeldOpenOrders() — same {role} base path + '/heldEcfInvestments'.
   * The list reconciles to `ecfInvestmentHoldAmount` server-side; render it, don't re-sum.
   */
  getHeldEcfInvestments(): Observable<HeldEcfInvestments> {
    if (this.storageService.role === Role.IssuerRep) {
      return EMPTY;
    }

    return this.http.get(this.route + '/heldEcfInvestments').pipe(
      map((data: any) => {
        return new HeldEcfInvestments(data.result);
      }),
    );
  }
}
